import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Lead,
  type LeadEventWithStatusDto,
  LeadQuerySortBy,
  LeadsApi,
  type LeadStatus,
  LeadContactChannel,
  LeadVisibility,
  Permission,
  QuerySortDirection,
  type UpdateLeadDto,
} from '@up2stay/up2promo-api-client';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { invalidateStatusBoardQueries } from '@/core/components/dashboard/dashboard-refresh-keys';
import {
  type ContactForm,
  type ContactFormField,
} from '@/core/components/leads/lead-detail/lead-detail-properties';
import {
  getInitialsFromDisplayName,
  getMemberDisplayName,
  getUserDisplayName,
  getUserInitials,
} from '@/core/components/leads/lead-detail/lead-user-display';
import { collectDistinctLeadTags } from '@/core/components/leads/lead-filter-normalize';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocale } from '@/core/hooks/use-locale';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useLeadDetailLookups } from '@/core/hooks/use-lead-detail-lookups';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { notificationUnreadCountQueryKey } from '@/core/hooks/use-notifications';
import {
  triggerErrorHaptic,
  triggerSuccessHaptic,
  triggerTapHaptic,
} from '@/core/lib/dnd-haptics';
import { openInSystemBrowser } from '@/core/lib/open-external-url';
import {
  buildEventNameMap,
  eventNamesFromLead,
} from '@/core/lib/lead-event-names';
import { fetchAllLeadEventsPages } from '@/core/lib/lead-events-fetch-all';
import { isValidLeadEmail } from '@/core/lib/lead-email';
import { fetchAllLeadsPages } from '@/core/lib/leads-fetch-all';
import {
  leadEventsQueryKeys,
  leadsQueryKeys,
} from '@/core/query-keys/leads-query-keys';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';

type UseLeadDetailOptions = {
  leadId: string | undefined;
  onDeleted?: () => void;
};

function contactFormFromLead(lead: Lead): ContactForm {
  return {
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    companyName: lead.companyName ?? '',
    jobTitle: lead.jobTitle ?? '',
    address: lead.address ?? '',
  };
}

const EMPTY_CONTACT_FORM: ContactForm = {
  email: '',
  phone: '',
  companyName: '',
  jobTitle: '',
  address: '',
};

/** Identity of server fields that drive the detail drafts. Not object identity. */
function leadDraftSourceKey(lead: Lead | undefined): string | null {
  if (!lead) return null;
  return [
    lead.id,
    lead.firstName ?? '',
    lead.lastName ?? '',
    lead.email ?? '',
    lead.phone ?? '',
    lead.companyName ?? '',
    lead.jobTitle ?? '',
    lead.address ?? '',
  ].join('\0');
}

export function useLeadDetail({ leadId, onDeleted }: UseLeadDetailOptions) {
  const dict = useDictionary();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const { isFeatureEnabled } = useEntitlements();
  const canReadLeads = can(Permission.LEADS_READ);
  const canSeeOthersPrivate = can(Permission.LEADS_READ_OTHERS);
  const canUpdateLeads = can(Permission.LEADS_UPDATE);
  const eventsEnabled = isFeatureEnabled('feature.lead_events');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isSavingCrmShare, setIsSavingCrmShare] = useState(false);
  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markIdle,
  } = useAutosaveCue();
  const [assigneeLabelOverride, setAssigneeLabelOverride] = useState<
    string | null
  >(null);
  const [firstNameDraft, setFirstNameDraft] = useState('');
  const [lastNameDraft, setLastNameDraft] = useState('');

  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isSavingEvents, setIsSavingEvents] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [contactForm, setContactForm] = useState<ContactForm>({
    email: '',
    phone: '',
    companyName: '',
    jobTitle: '',
    address: '',
  });
  /** Field message for a rejected email; the typed value stays (DEV-571). */
  const [contactEmailError, setContactEmailError] = useState<string | null>(
    null,
  );

  const [routeLeadId, setRouteLeadId] = useState(leadId);
  if (leadId !== routeLeadId) {
    setRouteLeadId(leadId);
    setShowDeleteDialog(false);
    setAssigneeLabelOverride(null);
  }

  const queryKey = useMemo(() => {
    if (!leadId) {
      return ['lead', leadId] as const;
    }
    return leadsQueryKeys.detail(leadId);
  }, [leadId]);

  const {
    data: lead,
    isLoading,
    isPending,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Lead> => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('workspaceId required');
      }
      const res = await LeadsApi.findOne({
        path: { workspaceId, id: leadId! },
      });
      if (!res.data) {
        throw new Error('Lead not found');
      }
      return res.data;
    },
    // The page stays mounted while `withRequiredPermissions` redirects, so
    // don't fire a request the API will reject.
    enabled: !!leadId && !!currentWorkspace && canReadLeads,
  });

  const draftSourceKey = leadDraftSourceKey(lead);
  const [hydratedDraftKey, setHydratedDraftKey] = useState<string | null>(null);
  if (draftSourceKey !== hydratedDraftKey) {
    setHydratedDraftKey(draftSourceKey);
    setContactEmailError(null);
    if (lead) {
      setFirstNameDraft(lead.firstName ?? '');
      setLastNameDraft(lead.lastName ?? '');
      setContactForm(contactFormFromLead(lead));
    } else {
      setFirstNameDraft('');
      setLastNameDraft('');
      setContactForm({ ...EMPTY_CONTACT_FORM });
    }
  }

  const detailDict = dict.leads.detail;
  const toastDict = detailDict.toast;
  const statusLabels = dict.leads.status as Record<string, string>;
  const unknownAuthorLabel = detailDict.activityUnknownAuthor;
  const currentUserDisplayName = getUserDisplayName(user);

  const {
    workspaceEvents,
    tagSuggestions,
    eventNameById,
    resolveAuthorDisplayName,
    resolveAuthorInitials,
    assigneeDisplayReadonly,
    canManageCrm,
    canEditEvents,
    canCreateEvents,
    canEditTags,
  } = useLeadDetailLookups({
    workspaceId: currentWorkspace?.id,
    lead,
    eventsEnabled,
    canUpdateLeads,
    workspaceLeadsAlwaysShared:
      currentWorkspace?.leadsAlwaysSharedWithWorkspace === true,
    locale,
    unknownAuthorLabel,
    assigneeLabelOverride,
  });

  const invalidateLeadQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root }),
      invalidateStatusBoardQueries(queryClient),
    ]);
  };

  const changeContactForm = (next: ContactForm) => {
    if (next.email !== contactForm.email) setContactEmailError(null);
    setContactForm(next);
  };

  /** Resolves false when the value was rejected and should stay editable. */
  const handleContactFieldBlur = async (
    field: ContactFormField,
    valueOverride?: string,
  ): Promise<boolean> => {
    if (!lead) return false;

    const trimmed = (valueOverride ?? contactForm[field]).trim();
    if (field === 'email' && !trimmed) {
      void triggerErrorHaptic();
      toast.error(dict.leads.form.errors.emailRequired);
      setContactForm((prev) => ({ ...prev, email: lead.email ?? '' }));
      setContactEmailError(null);
      return false;
    }
    if (field === 'email' && !isValidLeadEmail(trimmed)) {
      void triggerErrorHaptic();
      setContactEmailError(dict.leads.form.errors.emailInvalid);
      return false;
    }
    if (field === 'phone' && !trimmed) {
      void triggerErrorHaptic();
      toast.error(
        (dict.leads.form.errors as { phoneRequired?: string })?.phoneRequired ??
          'Phone is required',
      );
      setContactForm((prev) => ({ ...prev, phone: lead.phone ?? '' }));
      return false;
    }

    const optionalContactFields: ContactFormField[] = [
      'companyName',
      'jobTitle',
      'address',
    ];
    const current = lead[field] ?? '';
    if (field === 'email') setContactEmailError(null);
    if (trimmed === current) return true;

    try {
      setIsSavingContact(true);
      markSaving();
      await LeadsApi.update({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: {
          [field]: optionalContactFields.includes(field)
            ? trimmed
            : trimmed || null,
        } as Parameters<typeof LeadsApi.update>[0]['body'],
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
      markSaved();
      return true;
    } catch (error) {
      markIdle();
      void triggerErrorHaptic();
      if (field === 'email' && getApiValidationErrorCode(error, 'email')) {
        setContactEmailError(dict.leads.form.errors.emailInvalid);
        return false;
      }
      toast.error(toastDict.updateFailed);
      setContactForm({
        email: lead.email ?? '',
        phone: lead.phone ?? '',
        companyName: lead.companyName ?? '',
        jobTitle: lead.jobTitle ?? '',
        address: lead.address ?? '',
      });
      return false;
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleStatusChange = async (status: LeadStatus) => {
    if (!lead || lead.status === status) return;
    try {
      await LeadsApi.update({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: { status },
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
    } catch {
      void triggerErrorHaptic();
      toast.error(toastDict.statusUpdateFailed);
    }
  };

  const handleDescriptionSave = async (description: string) => {
    if (!lead) return;
    const current = lead.description ?? '';
    if (description === current) return;
    try {
      setIsSavingDescription(true);
      markSaving();
      await LeadsApi.update({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: { description: description || null },
      });
      await invalidateLeadQueries();
      markSaved();
    } catch {
      markIdle();
      void triggerErrorHaptic();
      toast.error(toastDict.updateFailed);
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleFirstNameBlur = async () => {
    if (!lead) return;
    const trimmed = firstNameDraft.trim();
    if (!trimmed) {
      setFirstNameDraft(lead.firstName ?? '');
      return;
    }
    if (trimmed === (lead.firstName ?? '')) return;
    try {
      setIsSavingName(true);
      markSaving();
      await LeadsApi.update({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: { firstName: trimmed } satisfies UpdateLeadDto,
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
      markSaved();
    } catch {
      markIdle();
      void triggerErrorHaptic();
      toast.error(toastDict.updateFailed);
      setFirstNameDraft(lead.firstName ?? '');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleLastNameBlur = async () => {
    if (!lead) return;
    const trimmed = lastNameDraft.trim();
    if (trimmed === (lead.lastName ?? '')) return;
    try {
      setIsSavingName(true);
      markSaving();
      await LeadsApi.update({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: { lastName: trimmed } satisfies UpdateLeadDto,
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
      markSaved();
    } catch {
      markIdle();
      void triggerErrorHaptic();
      toast.error(toastDict.updateFailed);
      setLastNameDraft(lead.lastName ?? '');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleAddComment = async (content: string) => {
    if (!lead) return;
    try {
      setIsSubmittingComment(true);
      await LeadsApi.addNote({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: { content },
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
    } catch {
      void triggerErrorHaptic();
      toast.error(toastDict.noteAddFailed);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  /** Author-only edit of a note (DEV-569); resolves false to keep the editor open. */
  const handleUpdateNote = async (
    noteId: string,
    content: string,
  ): Promise<boolean> => {
    if (!lead) return false;
    try {
      await LeadsApi.updateNote({
        path: {
          workspaceId: currentWorkspace?.id as string,
          id: lead.id,
          noteId,
        },
        body: { content },
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
      toast.success(toastDict.noteUpdated);
      return true;
    } catch {
      void triggerErrorHaptic();
      toast.error(toastDict.noteUpdateFailed);
      return false;
    }
  };

  /** Author-only delete of a note (DEV-569); rethrows so the confirm stays open. */
  const handleDeleteNote = async (noteId: string) => {
    if (!lead) return;
    try {
      await LeadsApi.removeNote({
        path: {
          workspaceId: currentWorkspace?.id as string,
          id: lead.id,
          noteId,
        },
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
      toast.success(toastDict.noteDeleted);
    } catch (error) {
      void triggerErrorHaptic();
      toast.error(toastDict.noteDeleteFailed);
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!lead) return;
    try {
      setIsDeleting(true);
      void triggerErrorHaptic();
      await LeadsApi.remove({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
      });
      void triggerSuccessHaptic();
      setShowDeleteDialog(false);
      onDeleted?.();
      const workspaceId = currentWorkspace?.id;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root }),
        invalidateStatusBoardQueries(queryClient),
        workspaceId
          ? queryClient.invalidateQueries({
              queryKey: ['notifications', 'inbox', workspaceId],
            })
          : Promise.resolve(),
        workspaceId
          ? queryClient.invalidateQueries({
              queryKey: notificationUnreadCountQueryKey(workspaceId),
            })
          : Promise.resolve(),
        workspaceId
          ? queryClient.invalidateQueries({
              queryKey: ['dashboard', 'leads', workspaceId],
            })
          : Promise.resolve(),
      ]);
    } catch {
      void triggerErrorHaptic();
      toast.error(toastDict.leadDeleteFailed);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetArchived = async (archived: boolean) => {
    if (!lead) return;
    try {
      setIsArchiving(true);
      await LeadsApi.update({
        path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
        body: { archived },
      });
      await invalidateLeadQueries();
      void triggerSuccessHaptic();
    } catch {
      void triggerErrorHaptic();
      toast.error(
        archived
          ? (dict.leads.list.contextMenu?.archiveError ??
              toastDict.updateFailed)
          : (dict.leads.list.contextMenu?.unarchiveError ??
              toastDict.updateFailed),
      );
    } finally {
      setIsArchiving(false);
    }
  };

  const handleCrmAssigneeChange = useCallback(
    (nextUserId: string | null, selectedLabel: string) => {
      if (!lead || !canManageCrm) return;
      const normalized =
        typeof nextUserId === 'string' && nextUserId.trim() !== ''
          ? nextUserId.trim()
          : null;
      const currentNorm = lead.assigneeUserId?.trim()
        ? lead.assigneeUserId.trim()
        : null;
      if (normalized === currentNorm) return;

      const previousLead = lead;
      const previousLabelOverride = assigneeLabelOverride;

      setAssigneeLabelOverride(selectedLabel);
      const nextVisibility =
        normalized !== null &&
        // Ownerless leads (owner left, ADR-0017) are already workspace-visible.
        normalized !== (lead.userId?.trim() ?? '') &&
        lead.visibility !== LeadVisibility.WORKSPACE
          ? LeadVisibility.WORKSPACE
          : undefined;
      queryClient.setQueryData<Lead>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          assigneeUserId: normalized,
          ...(nextVisibility !== undefined
            ? { visibility: nextVisibility }
            : {}),
        };
      });

      void (async () => {
        try {
          markSaving();
          await LeadsApi.update({
            path: {
              workspaceId: currentWorkspace?.id as string,
              id: lead.id,
            },
            body: {
              assigneeUserId: normalized,
              ...(nextVisibility !== undefined
                ? { visibility: nextVisibility }
                : {}),
            },
          });
          void queryClient.invalidateQueries({ queryKey });
          void queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root });
          markSaved();
        } catch {
          queryClient.setQueryData(queryKey, previousLead);
          setAssigneeLabelOverride(previousLabelOverride);
          markIdle();
          void triggerErrorHaptic();
          toast.error(toastDict.updateFailed);
        }
      })();
    },
    [
      assigneeLabelOverride,
      canManageCrm,
      currentWorkspace,
      lead,
      markIdle,
      markSaved,
      markSaving,
      queryClient,
      queryKey,
      toastDict.updateFailed,
    ],
  );

  const handleCrmShareChange = useCallback(
    async (next: boolean) => {
      if (!lead || !canManageCrm) return;
      const current = lead.visibility === LeadVisibility.WORKSPACE;
      if (next === current) return;
      try {
        setIsSavingCrmShare(true);
        markSaving();
        await LeadsApi.update({
          path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
          body: {
            visibility: next
              ? LeadVisibility.WORKSPACE
              : LeadVisibility.PRIVATE,
          },
        });
        await queryClient.invalidateQueries({ queryKey });
        await queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root });
        void triggerSuccessHaptic();
        markSaved();
      } catch {
        markIdle();
        void triggerErrorHaptic();
        toast.error(toastDict.updateFailed);
      } finally {
        setIsSavingCrmShare(false);
      }
    },
    [
      lead,
      canManageCrm,
      currentWorkspace,
      queryClient,
      queryKey,
      toastDict.updateFailed,
      markSaving,
      markSaved,
      markIdle,
    ],
  );

  const handleContactVia = useCallback(
    async (channel: LeadContactChannel) => {
      if (!lead || !currentWorkspace?.id) return;
      const phone = lead.phone?.trim();
      const email = lead.email?.trim();
      if (channel === LeadContactChannel.CALL && phone) {
        void openInSystemBrowser(`tel:${phone}`);
      } else if (channel === LeadContactChannel.SMS && phone) {
        void openInSystemBrowser(`sms:${phone}`);
      } else if (channel === LeadContactChannel.EMAIL && email) {
        void openInSystemBrowser(`mailto:${email}`);
      }
      try {
        const res = await LeadsApi.contactVia({
          path: { workspaceId: currentWorkspace.id, id: lead.id },
          body: { channel },
        });
        if (res.data) {
          queryClient.setQueryData(queryKey, res.data);
        }
      } catch {
        // Logging is best-effort; the OS handoff already happened.
      }
    },
    [lead, currentWorkspace, queryClient, queryKey],
  );

  const handleEventsChange = useCallback(
    async (nextEventIds: string[]) => {
      if (!lead || !canEditEvents) return;
      try {
        setIsSavingEvents(true);
        markSaving();
        const res = await LeadsApi.update({
          path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
          body: {
            eventIds: nextEventIds,
          } as Parameters<typeof LeadsApi.update>[0]['body'],
        });
        queryClient.setQueryData(queryKey, res.data);
        markSaved();
      } catch (error: unknown) {
        markIdle();
        console.error('Failed to update lead events', error);
        toast.error(detailDict.toast.updateFailed);
      } finally {
        setIsSavingEvents(false);
      }
    },
    [
      lead,
      canEditEvents,
      currentWorkspace,
      queryClient,
      queryKey,
      detailDict.toast,
      markSaving,
      markSaved,
      markIdle,
    ],
  );

  const handleTagsChange = useCallback(
    async (nextTags: string[]) => {
      if (!lead || !canEditTags) return;
      try {
        setIsSavingTags(true);
        markSaving();
        const res = await LeadsApi.update({
          path: { workspaceId: currentWorkspace?.id as string, id: lead.id },
          body: {
            tags: nextTags,
          } as Parameters<typeof LeadsApi.update>[0]['body'],
        });
        queryClient.setQueryData(queryKey, res.data);
        void queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root });
        markSaved();
      } catch (error: unknown) {
        markIdle();
        console.error('Failed to update lead tags', error);
        toast.error(detailDict.toast.updateFailed);
      } finally {
        setIsSavingTags(false);
      }
    },
    [
      lead,
      canEditTags,
      currentWorkspace,
      queryClient,
      queryKey,
      detailDict.toast,
      markSaving,
      markSaved,
      markIdle,
    ],
  );

  const handlePropertyCopy = () => {
    void triggerTapHaptic();
  };

  const handleLeadUpdatedInCache = useCallback(
    (updated: Lead) => {
      queryClient.setQueryData(queryKey, updated);
    },
    [queryClient, queryKey],
  );

  const handleEventsAdd = useCallback(
    (eventId: string) => {
      if (!lead) return;
      const next = [...new Set([...(lead.eventIds ?? []), eventId])];
      void (async () => {
        await queryClient.invalidateQueries({
          queryKey: currentWorkspace?.id
            ? leadEventsQueryKeys.all(currentWorkspace.id)
            : leadEventsQueryKeys.root,
        });
        await handleEventsChange(next);
      })();
    },
    [lead, queryClient, currentWorkspace, handleEventsChange],
  );

  const handleEventsRemove = useCallback(
    (eventId: string) => {
      if (!lead) return;
      const next = (lead.eventIds ?? []).filter((id) => id !== eventId);
      void handleEventsChange(next);
    },
    [lead, handleEventsChange],
  );

  const handleTagAdd = useCallback(
    (tag: string) => {
      if (!lead) return;
      const next = [...new Set([...(lead.tags ?? []), tag])];
      void handleTagsChange(next);
    },
    [lead, handleTagsChange],
  );

  const handleTagRemove = useCallback(
    (tag: string) => {
      if (!lead) return;
      const key = tag.toLowerCase();
      const next = (lead.tags ?? []).filter(
        (t) => String(t).trim().toLowerCase() !== key,
      );
      void handleTagsChange(next);
    },
    [lead, handleTagsChange],
  );

  /** Use `currentWorkspace` for member listing — `lead.workspaceId` can be missing or stale. */
  const crmWorkspaceId =
    currentWorkspace?.id?.trim() || lead?.workspaceId?.trim() || '';

  return {
    dict,
    locale,
    detailDict,
    statusLabels,
    unknownAuthorLabel,
    currentWorkspace,
    queryKey,
    lead,
    isLoading,
    isPending,
    isFetched,
    error,
    refetch,
    autosaveStatus,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
    isArchiving,
    isSavingDescription,
    isSavingName,
    isSubmittingComment,
    isSavingCrmShare,
    isSavingContact,
    isSavingEvents,
    isSavingTags,
    firstNameDraft,
    setFirstNameDraft,
    lastNameDraft,
    setLastNameDraft,
    contactForm,
    setContactForm,
    changeContactForm,
    contactEmailError,
    workspaceEvents,
    tagSuggestions,
    eventNameById,
    resolveAuthorDisplayName,
    resolveAuthorInitials,
    assigneeDisplayReadonly,
    canManageCrm,
    canEditEvents,
    canCreateEvents,
    canEditTags,
    eventsEnabled,
    crmWorkspaceId,
    handleContactFieldBlur,
    handleStatusChange,
    handleDescriptionSave,
    handleFirstNameBlur,
    handleLastNameBlur,
    handleAddComment,
    handleUpdateNote,
    handleDeleteNote,
    currentUserId: user?.id ?? null,
    handleDelete,
    handleSetArchived,
    handleCrmAssigneeChange,
    handleCrmShareChange,
    handleContactVia,
    handlePropertyCopy,
    handleLeadUpdatedInCache,
    handleEventsAdd,
    handleEventsRemove,
    handleTagAdd,
    handleTagRemove,
  };
}
